/**
 * Onboarding wizard actions: the seven AI enrichments a new user can pick in the
 * chat wizard, run once each on the first rows of their first saved search.
 *
 * The free run is decided server-side (backend onboarding_free_run.py): the
 * client sends no "free" flag, only the exact wizard query, the wizard model and
 * at most ONBOARDING_ACTION_ROW_LIMIT rows. Keep the queries byte-identical to
 * the backend's WIZARD_AI_QUERIES or the run is silently charged.
 */

const wizardAction = (id, query) => ({ id, label: query, action_type: "ai_enrichment", query });

export const ONBOARDING_WIZARD_ACTIONS = [
  wizardAction("company-owner", "Find the company owner"),
  wizardAction("decision-makers", "Identify key decision makers"),
  wizardAction("business-summary", "Summarize the business"),
  wizardAction("contact-info", "Find company contact information"),
  wizardAction("website-analysis", "Analyze the company website"),
  wizardAction("business-model", "Identify the company's business model"),
  wizardAction("similar-companies", "Find similar companies"),
];

export const ONBOARDING_ACTION_ROW_LIMIT = 5;
export const ONBOARDING_ACTION_PROVIDER = "gemini";
export const ONBOARDING_ACTION_MODEL = "gemini-3.8-flash";

const TERMINAL_BATCH_STATUSES = ["completed", "failed", "cancelled"];

export const isTerminalBatchStatus = (status) => TERMINAL_BATCH_STATUSES.includes(status);

/** Catalog actions for the chip labels the user picked, in catalog order. */
export function onboardingActionsFromLabels(labels) {
  const picked = new Set((labels || []).map((label) => String(label).trim().toLowerCase()));
  return ONBOARDING_WIZARD_ACTIONS.filter((action) => picked.has(action.label.toLowerCase()));
}

const RUN_KEY = "onboardingActionRun";

/**
 * Tab-local marker that a wizard run was requested for this saved search.
 * The backend reports only the batches that consumed the free entitlement
 * (onboarding_free_run.py accepted_batches), so `submitted` also remembers
 * batches this tab started, so a reload never submits them twice.
 */
export function readOnboardingActionRun(workspaceId) {
  try {
    const run = JSON.parse(sessionStorage.getItem(RUN_KEY) || "null");
    if (!run || run.workspaceId !== workspaceId) return null;
    return { workspaceId, submitted: run.submitted || {} };
  } catch {
    return null;
  }
}

const writeRun = (run) => {
  try {
    sessionStorage.setItem(RUN_KEY, JSON.stringify(run));
  } catch {
    // Storage unavailable: the server-side accepted batches still guard resubmits.
  }
};

export const startOnboardingActionRun = (workspaceId) => writeRun({ workspaceId, submitted: {} });

export function recordOnboardingActionSubmission(workspaceId, query, batchId) {
  const run = readOnboardingActionRun(workspaceId);
  if (!run) return;
  writeRun({ ...run, submitted: { ...run.submitted, [query]: batchId } });
}

export const clearOnboardingActionRun = () => {
  try {
    sessionStorage.removeItem(RUN_KEY);
  } catch {
    // ignore
  }
};

/**
 * What the saved search should run, from GET /user/onboarding/ plus the local
 * marker. `accepted` maps each already-submitted query to its batch.
 */
export function planOnboardingActionRun(progress, workspaceId, localRun = null) {
  const answers = progress?.answers;
  if (!answers || answers.workspace_id !== workspaceId) return { actions: [], accepted: {} };
  const accepted = {};
  Object.entries(localRun?.submitted || {}).forEach(([query, batchId]) => {
    accepted[query] = { batchId, status: "pending" };
  });
  (progress.accepted_batches || []).forEach((batch) => {
    accepted[batch.query] = { batchId: batch.batch_id, status: batch.status };
  });
  return { actions: answers.selected_actions || [], accepted };
}
